import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRegionStore } from '@/stores/region-store'
import {
  getApiV1TukuPictureTypesByRegionYears,
  getApiV1TukuPictureTypesByRegionPeriods,
} from '@/api'
import type { TukuImages } from '@/api'
import { fetchDrawBalls } from '@/components/lottery-ball-row'
import type { DrawBalls } from '@/components/lottery-ball-row'

/**
 * 图库某年某期的开奖号码（开奖记录 id = year*10000 + period*10 + 彩种号，港彩 1、澳彩 2）。
 * 后端对「找不到记录」会回 500（应改为 200 + record=null），这里兜底成 null，避免整页空白。
 */
export async function fetchTukuDrawBalls(
  region: 'hk' | 'aocai',
  year: number,
  period: number,
): Promise<DrawBalls | null> {
  const lotteryTypeNum = region === 'hk' ? 1 : 2
  try {
    return await fetchDrawBalls(region, year * 10000 + period * 10 + lotteryTypeNum)
  } catch {
    return null
  }
}

export function useGalleryPeriod(
  getPictureTypeId: () => string,
  onPeriodSelected: (year: number, period: number) => Promise<void>,
) {
  const route = useRoute()
  const router = useRouter()
  const regionStore = useRegionStore()

  const availableYears = ref<number[]>([])
  const periodList = ref<TukuImages[]>([])
  const selectedYear = ref(new Date().getFullYear())
  const selectedPeriod = ref<number | null>(null)

  const yearList = computed(() =>
    availableYears.value.map((y) => ({ id: y, name: `${y}年` })),
  )

  const periodOptionList = computed(() =>
    periodList.value.map((p) => ({
      id: p.period ?? 0,
      name: p.name ?? `第${String(p.period).padStart(3, '0')}期`,
    })),
  )

  async function fetchYears() {
    const res = await getApiV1TukuPictureTypesByRegionYears({
      path: { region: regionStore.region },
      query: {
        pictureTypeId: Number(getPictureTypeId())
      }
    })
    availableYears.value = res.data?.data?.list ?? []
  }

  async function fetchPeriods(year: number) {
    const res = await getApiV1TukuPictureTypesByRegionPeriods({
      path: { region: regionStore.region },
      query: { year, pictureTypeId: Number(getPictureTypeId()) },
    })
    periodList.value = res.data?.data?.list ?? []
  }

  function syncToRoute() {
    router.replace({
      query: {
        ...route.query,
        year: selectedYear.value,
        period: selectedPeriod.value ?? undefined,
        region: regionStore.region,
      },
    })
  }

  async function handleYearChange(yearId: number | string) {
    selectedYear.value = Number(yearId)
    await fetchPeriods(Number(yearId))
    const first = periodList.value[0]
    if (first?.period != null) {
      selectedPeriod.value = first.period
      await onPeriodSelected(Number(yearId), first.period)
    }
    syncToRoute()
  }

  async function handlePeriodChange(periodId: number | string) {
    const p = Number(periodId)
    selectedPeriod.value = p
    await onPeriodSelected(selectedYear.value, p)
    syncToRoute()
  }

  // 从 route.query 读取 year/period 初始化，需先调用 fetchYears()
  async function initFromRoute() {
    const qYear = Number(route.query.year)
    const qPeriod = Number(route.query.period)
    const initYear =
      qYear > 2000 && availableYears.value.includes(qYear) ? qYear : availableYears.value[0]
    if (!initYear) return
    selectedYear.value = initYear
    await fetchPeriods(initYear)
    const validPeriod = periodList.value.find((p) => p.period === qPeriod)
    const firstPeriod = validPeriod ?? periodList.value[0]
    if (firstPeriod?.period != null) {
      selectedPeriod.value = firstPeriod.period
      await onPeriodSelected(initYear, firstPeriod.period)
    }
    syncToRoute()
  }

  return {
    availableYears,
    periodList,
    selectedYear,
    selectedPeriod,
    yearList,
    periodOptionList,
    fetchYears,
    syncToRoute,
    handleYearChange,
    handlePeriodChange,
    initFromRoute,
  }
}
